import * as React from 'react';
import { buildClassName } from 'utils/build-class-name';
import styles from './accordion.module.scss';

interface AccordionContextValue {
  value: string | null;
  onValueChange: (value: string | null) => void;
}

const AccordionContext = React.createContext<AccordionContextValue | undefined>(undefined);

interface AccordionProps {
  type?: 'single';
  collapsible?: boolean;
  value?: string;
  onValueChange?: (value: string | null) => void;
  children: React.ReactNode;
  className?: string;
}

const Accordion = ({ type: _type, collapsible: _collapsible, value: controlledValue, onValueChange, children, className }: AccordionProps) => {
  const [internalValue, setInternalValue] = React.useState<string | null>(null);
  const value = controlledValue !== undefined ? controlledValue : internalValue;
  const handleValueChange = (newValue: string | null) => {
    if (onValueChange) {
      onValueChange(newValue);
    } else {
      setInternalValue(newValue);
    }
  };

  return (
    <AccordionContext.Provider value={{ value, onValueChange: handleValueChange }}>
      <div className={buildClassName(styles.accordion, className)}>{children}</div>
    </AccordionContext.Provider>
  );
};

const AccordionItem = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & { value: string }
>(({ className, value, children, ...props }, ref) => {
  return (
    <div ref={ref} className={buildClassName(styles.accordionItem, className)} {...props}>
      {children}
    </div>
  );
});
AccordionItem.displayName = 'AccordionItem';

const AccordionTrigger = React.forwardRef<
  HTMLButtonElement,
  React.ButtonHTMLAttributes<HTMLButtonElement> & { value: string }
>(({ className, value, children, ...props }, ref) => {
  const context = React.useContext(AccordionContext);
  if (!context) throw new Error('AccordionTrigger must be used within Accordion');

  const isOpen = context.value === value;

  return (
    <button
      ref={ref}
      className={buildClassName(styles.accordionTrigger, className)}
      onClick={() => context.onValueChange(isOpen ? null : value)}
      {...props}
    >
      {children}
      <svg
        className={buildClassName(styles.icon, isOpen && styles.open)}
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
      </svg>
    </button>
  );
});
AccordionTrigger.displayName = 'AccordionTrigger';

const AccordionContent = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & { value: string }
>(({ className, value, children, ...props }, ref) => {
  const context = React.useContext(AccordionContext);
  if (!context) throw new Error('AccordionContent must be used within Accordion');

  const isOpen = context.value === value;

  if (!isOpen) return null;

  return (
    <div
      ref={ref}
      className={buildClassName(styles.accordionContent, className)}
      {...props}
    >
      <div className={styles.accordionContentInner}>{children}</div>
    </div>
  );
});
AccordionContent.displayName = 'AccordionContent';

export { Accordion, AccordionItem, AccordionTrigger, AccordionContent };
